import { useEffect, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import DOMPurify from "dompurify";
import { api } from "@/lib/api";
import { Icon } from "./Icon";
import { kindLabel } from "@/lib/docTemplates";
import type { DocKind } from "@shared/types";

type ProjectHit = { id: string; name: string };
type TaskHit = { id: string; project_id: string; title: string; snip: string };
type CommentHit = {
  id: string;
  task_id: string;
  project_id: string;
  snip: string;
};
type DocHit = {
  id: string;
  project_id: string;
  kind: string;
  title: string;
};
type WikiHit = {
  id: string;
  project_id: string;
  slug: string;
  title: string;
};
type Result = {
  projects: ProjectHit[];
  tasks: TaskHit[];
  comments: CommentHit[];
  documents: DocHit[];
  wiki: WikiHit[];
};

// FTS5 snippets contain <mark>…</mark> around matched terms plus the original
// document content (titles/bodies are user-authored). Sanitise via DOMPurify
// with an allowlist of only <mark>; everything else is stripped/escaped before
// it ever hits the DOM.
const PURE_MARK = { ALLOWED_TAGS: ["mark"], ALLOWED_ATTR: [] };
function safeSnip(html: string): string {
  return DOMPurify.sanitize(html, PURE_MARK);
}

export function CommandPalette({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState<Result>({
    projects: [],
    tasks: [],
    comments: [],
    documents: [],
    wiki: [],
  });
  const navigate = useNavigate();
  const restoreFocusTo = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!q || q.length < 2) {
      setResults({
        projects: [],
        tasks: [],
        comments: [],
        documents: [],
        wiki: [],
      });
      return;
    }
    const id = setTimeout(async () => {
      try {
        // api.search throws ApiError on non-2xx; preserve the original
        // behaviour of silently keeping the previous results on failure.
        const r = await api.search(q);
        setResults(r);
      } catch {
        // ignore — keep previous results on transient failures
      }
    }, 200);
    return () => clearTimeout(id);
  }, [q]);

  // Save the trigger (the element that opened the dialog) on open, then
  // hand focus back on close so keyboard users land where they started. Also
  // close on Escape — palette is a modal so Esc is the conventional exit.
  useEffect(() => {
    if (!open) return;
    restoreFocusTo.current = document.activeElement as HTMLElement | null;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      restoreFocusTo.current?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  const hasResults =
    results.tasks.length +
      results.projects.length +
      results.comments.length +
      results.documents.length +
      results.wiki.length >
    0;

  return (
    <div
      className="cmd-scrim"
      role="dialog"
      aria-modal="true"
      aria-label="Search"
      onClick={onClose}
    >
      <div className="cmd-panel" onClick={(e) => e.stopPropagation()}>
        <div className="cmd-search">
          <Icon name="search" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search projects, tasks, comments, docs…"
            aria-label="Search"
          />
          <span className="cmd-esc">Esc</span>
        </div>
        <ul className="cmd-results">
          {!hasResults && q.length >= 2 && (
            <li className="cmd-empty">No matches.</li>
          )}
          {!hasResults && q.length < 2 && (
            <li className="cmd-empty">
              Type at least 2 characters to search projects, tasks, and
              comments.
            </li>
          )}
          {results.tasks.length > 0 && <li className="cmd-group-h">Tasks</li>}
          {results.tasks.map((t) => (
            <li key={t.id}>
              <button
                type="button"
                className="cmd-row"
                onClick={() => {
                  onClose();
                  navigate({
                    to: "/tasks/$taskId",
                    params: { taskId: t.id },
                  });
                }}
              >
                <span className="cmd-ic">
                  <Icon name="check" />
                </span>
                <span className="cmd-main">
                  <div className="cmd-title">{t.title}</div>
                  <div
                    className="cmd-snip"
                    dangerouslySetInnerHTML={{ __html: safeSnip(t.snip) }}
                  />
                </span>
                <span className="cmd-kind">Task</span>
              </button>
            </li>
          ))}
          {results.projects.length > 0 && (
            <li className="cmd-group-h">Projects</li>
          )}
          {results.projects.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                className="cmd-row"
                onClick={() => {
                  onClose();
                  navigate({
                    to: "/projects/$projectId",
                    params: { projectId: p.id },
                  });
                }}
              >
                <span className="cmd-ic">
                  <Icon name="folder" />
                </span>
                <span className="cmd-main">
                  <div className="cmd-title">{p.name}</div>
                </span>
                <span className="cmd-kind">Project</span>
              </button>
            </li>
          ))}
          {results.comments.length > 0 && (
            <li className="cmd-group-h">Comments</li>
          )}
          {results.comments.map((cmt) => (
            <li key={cmt.id}>
              <button
                type="button"
                className="cmd-row"
                onClick={() => {
                  onClose();
                  navigate({
                    to: "/tasks/$taskId",
                    params: { taskId: cmt.task_id },
                  });
                }}
              >
                <span className="cmd-ic">
                  <Icon name="chat" />
                </span>
                <span className="cmd-main">
                  <div
                    className="cmd-snip"
                    dangerouslySetInnerHTML={{ __html: safeSnip(cmt.snip) }}
                  />
                </span>
                <span className="cmd-kind">Comment</span>
              </button>
            </li>
          ))}
          {results.documents.length > 0 && (
            <li className="cmd-group-h">Docs</li>
          )}
          {results.documents.map((d) => (
            <li key={d.id}>
              <button
                type="button"
                className="cmd-row"
                onClick={() => {
                  onClose();
                  navigate({
                    to: "/projects/$projectId/docs",
                    params: { projectId: d.project_id },
                    search: { doc: d.id },
                  });
                }}
              >
                <span className="cmd-ic">
                  <Icon name="clip" />
                </span>
                <span className="cmd-main">
                  <div className="cmd-title">{d.title}</div>
                </span>
                <span className="cmd-kind">{kindLabel(d.kind as DocKind)}</span>
              </button>
            </li>
          ))}
          {results.wiki.length > 0 && <li className="cmd-group-h">Wiki</li>}
          {results.wiki.map((w) => (
            <li key={w.id}>
              <button
                type="button"
                className="cmd-row"
                onClick={() => {
                  onClose();
                  navigate({
                    to: "/projects/$projectId/wiki",
                    params: { projectId: w.project_id },
                    search: { page: w.slug },
                  });
                }}
              >
                <span className="cmd-ic">
                  <Icon name="folder" />
                </span>
                <span className="cmd-main">
                  <div className="cmd-title">{w.title}</div>
                </span>
                <span className="cmd-kind">Wiki</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
